import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useFormatting } from '@/hooks/useFormatting';
import { Badge } from '@/components/ui/badge';
import { Building2, CreditCard, Wallet, Lock, TrendingUp, Info } from 'lucide-react';
import type { BankAccount, RestrictedFund, CreditFacility } from '@/types';

interface CashBreakdownModalProps {
  open: boolean;
  onClose: () => void;
  accounts: BankAccount[];
  totalCash: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAccountIcon(type?: string) {
  if (type === 'payroll') return <Wallet className="w-5 h-5 text-primary" />;
  if (type === 'Savings') return <CreditCard className="w-5 h-5 text-primary" />;
  return <Building2 className="w-5 h-5 text-primary" />;
}

function restrictionLabel(type: RestrictedFund['restrictionType']): string {
  switch (type) {
    case 'client_money':     return 'Client money';
    case 'fixed_term':       return 'Fixed term';
    case 'internal_reserve': return 'Internal reserve';
  }
}

function facilityLabel(type: CreditFacility['type']): string {
  if (type === 'overdraft') return 'Overdraft';
  return 'Credit facility';
}

// ─── Main component ───────────────────────────────────────────────────────────

export const CashBreakdownModal: React.FC<CashBreakdownModalProps> = ({
  open,
  onClose,
  accounts,
  totalCash,
}) => {
  const { formatCurrency } = useFormatting();

  // Derive composition figures from account data
  const totalRestricted = accounts.reduce((sum, acc) => {
    if (!acc.restrictedFunds) return sum;
    return sum + acc.restrictedFunds.reduce((s, r) => s + r.restrictedAmount, 0);
  }, 0);
  const availableCash = totalCash - totalRestricted;

  const totalFacilityLimit = accounts.reduce(
    (sum, acc) => sum + (acc.creditFacilities ?? []).reduce((s, f) => s + (f.facilityLimit ?? 0), 0), 0
  );
  const totalFacilityDrawn = accounts.reduce(
    (sum, acc) => sum + (acc.creditFacilities ?? []).reduce((s, f) => s + (f.facilityDrawn ?? 0), 0), 0
  );
  const totalFacilityHeadroom = totalFacilityLimit - totalFacilityDrawn;
  const maxAvailableCash = availableCash + totalFacilityHeadroom;

  const hasFacilities  = totalFacilityLimit > 0;
  const hasRestrictions = totalRestricted > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cash Position Breakdown</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">

          {/* ── Cash composition summary ───────────────────────────────── */}
          <div className="bg-primary/5 rounded-lg p-5 border-2 border-primary/20 space-y-4">
            {/* Ledger balance */}
            <div>
              <div className="text-sm text-muted-foreground mb-0.5">Total ledger balance</div>
              <div className="text-4xl font-bold text-primary">{formatCurrency(totalCash, 'full')}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Across {accounts.length} bank account{accounts.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Composition breakdown */}
            {(hasRestrictions || hasFacilities) && (
              <div className="border-t pt-4 space-y-2">
                {hasRestrictions && (
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Lock className="w-3.5 h-3.5" />
                      Less: restricted / protected funds
                    </div>
                    <span className="font-medium text-destructive">
                      − {formatCurrency(totalRestricted, 'full')}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm font-semibold border-t pt-2">
                  <span>Available cash (usable today)</span>
                  <span className="text-foreground">{formatCurrency(availableCash, 'full')}</span>
                </div>

                {hasFacilities && (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <TrendingUp className="w-3.5 h-3.5" />
                        Plus: undrawn facility headroom
                      </div>
                      <span className="font-medium text-emerald-600">
                        + {formatCurrency(totalFacilityHeadroom, 'full')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm font-semibold bg-emerald-50 dark:bg-emerald-950/30 rounded px-2 py-1.5">
                      <span className="text-emerald-700 dark:text-emerald-400">
                        Maximum available (inc. facility)
                      </span>
                      <span className="text-emerald-700 dark:text-emerald-400">
                        {formatCurrency(maxAvailableCash, 'full')}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Account-by-account breakdown ──────────────────────────── */}
          <div className="space-y-3">
            <h3 className="font-semibold text-base">Bank Accounts</h3>
            <div className="space-y-3">
              {accounts.map((account) => {
                const accountRestricted = (account.restrictedFunds ?? []).reduce(
                  (s, r) => s + r.restrictedAmount, 0
                );
                const accountAvailable = account.balance - accountRestricted;
                const accountFacilities = account.creditFacilities ?? [];
                const facilityHeadroom = accountFacilities.reduce(
                  (s, f) => s + (f.facilityLimit - f.facilityDrawn), 0
                );

                return (
                  <div key={account.id} className="border rounded-lg overflow-hidden">
                    {/* Account header row */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          {getAccountIcon(account.type)}
                        </div>
                        <div>
                          <div className="font-medium">{account.name}</div>
                          <div className="text-sm text-muted-foreground">{account.bank}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-lg">{formatCurrency(account.balance, 'full')}</div>
                        <Badge variant="outline" className="mt-1">{account.type}</Badge>
                      </div>
                    </div>

                    {/* Restricted funds (if any) */}
                    {account.restrictedFunds && account.restrictedFunds.length > 0 && (
                      <div className="border-t bg-amber-50/50 dark:bg-amber-950/20 px-4 py-3 space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide">
                          <Lock className="w-3 h-3" />
                          Restricted funds
                        </div>
                        {account.restrictedFunds.map((r) => (
                          <div key={r.id} className="flex items-start justify-between text-sm">
                            <div>
                              <span className="text-foreground">{r.label}</span>
                              <div className="text-xs text-muted-foreground mt-0.5 space-x-2">
                                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                  {restrictionLabel(r.restrictionType)}
                                </Badge>
                                {r.releaseDate && (
                                  <span>
                                    Releases {r.releaseDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </span>
                                )}
                              </div>
                            </div>
                            <span className="font-medium text-destructive shrink-0 ml-4">
                              − {formatCurrency(r.restrictedAmount, 'full')}
                            </span>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm font-semibold pt-1 border-t">
                          <span>Available from this account</span>
                          <span>{formatCurrency(accountAvailable, 'full')}</span>
                        </div>
                      </div>
                    )}

                    {/* Credit facilities (if any) */}
                    {accountFacilities.length > 0 && (
                      <div className="border-t bg-emerald-50/50 dark:bg-emerald-950/20 px-4 py-3 space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 uppercase tracking-wide">
                          <TrendingUp className="w-3 h-3" />
                          Credit facilities
                        </div>
                        {accountFacilities.map((facility) => (
                          <div key={facility.id} className="space-y-2">
                            <div className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                              {facilityLabel(facility.type)} facility
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-sm">
                              <div>
                                <div className="text-muted-foreground text-xs mb-0.5">Facility limit</div>
                                <div className="font-medium">{formatCurrency(facility.facilityLimit, 'full')}</div>
                              </div>
                              <div>
                                <div className="text-muted-foreground text-xs mb-0.5">Currently drawn</div>
                                <div className="font-medium text-amber-600 dark:text-amber-400">
                                  {formatCurrency(facility.facilityDrawn, 'full')}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted-foreground text-xs mb-0.5">Headroom available</div>
                                <div className="font-medium text-emerald-600 dark:text-emerald-400">
                                  {formatCurrency(facility.facilityLimit - facility.facilityDrawn, 'full')}
                                </div>
                              </div>
                            </div>
                            {(facility.interestRateSchedule?.length ?? 0) > 0 && (
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Info className="w-3 h-3" />
                                {(() => {
                                  const schedule = facility.interestRateSchedule!;
                                  const now = new Date();
                                  const current = [...schedule]
                                    .filter(e => new Date(e.effectiveFrom) <= now)
                                    .sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime())[0];
                                  return current ? `${current.rate}% p.a.` : null;
                                })()}
                                {facility.facilityReviewDate && (
                                  <> · Next review {facility.facilityReviewDate.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</>
                                )}
                                {facility.securedOn && (
                                  <> · Secured on {facility.securedOn}</>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Quick stats ────────────────────────────────────────────── */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div>
              <div className="text-sm text-muted-foreground">Largest account</div>
              <div className="font-semibold">
                {formatCurrency(Math.max(...accounts.map((a) => a.balance)), 'full')}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Smallest account</div>
              <div className="font-semibold">
                {formatCurrency(Math.min(...accounts.map((a) => a.balance)), 'full')}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Average balance</div>
              <div className="font-semibold">
                {formatCurrency(totalCash / accounts.length, 'full')}
              </div>
            </div>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};
